import { c as _c } from "react/compiler-runtime";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { parseError } from "@/lib/parse-error";
import { toast } from "@/lib/use-toast";
import type { FullscriptConnectionStatus, FullscriptTreatmentPlanListResponse } from "@/types/fullscript";
import { connectFullscript, disconnectFullscript, fetchFullscriptStatus, fetchFullscriptTreatmentPlans, linkFullscriptPatient, syncFullscriptTreatmentPlans, unlinkFullscriptPatient } from "@/api/provider/fullscript";
export const fullscriptQueryKeys = {
  all: ["fullscript"] as const,
  connection: () => [...fullscriptQueryKeys.all, "connection"] as const,
  treatmentPlans: (patientId: string) => [...fullscriptQueryKeys.all, "treatment-plans", patientId] as const
};
export const fullscriptQueryOptions = {
  connection: () => ({
    queryKey: fullscriptQueryKeys.connection(),
    queryFn: fetchFullscriptStatus
  })
};
export function useFullscriptStatus() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = fullscriptQueryOptions.connection();
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return useQuery(t0);
}
export function useConnectFullscriptMutation() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      mutationFn: connectFullscript,
      onError: _temp
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return useMutation(t0);
}
async function _temp(error) {
  toast({
    title: "Error",
    description: await parseError(error, "Failed to start Fullscript authorization."),
    variant: "error"
  });
}
export function useDisconnectFullscriptMutation() {
  const $ = _c(2);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== queryClient) {
    t0 = {
      mutationFn: disconnectFullscript,
      onSuccess: () => {
        queryClient.setQueryData(fullscriptQueryKeys.connection(), {
          connected: false
        });
      },
      onError: _temp2
    };
    $[0] = queryClient;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return useMutation(t0);
}
async function _temp2(error) {
  toast({
    title: "Error",
    description: await parseError(error, "Failed to disconnect Fullscript."),
    variant: "error"
  });
}
export function useLinkFullscriptPatientMutation(patientId) {
  const $ = _c(9);
  const queryClient = useQueryClient();
  const {
    mutate: syncPlans
  } = useSyncFullscriptTreatmentPlansMutation(patientId);
  let t0;
  if ($[0] !== patientId) {
    t0 = fullscriptPatientId => linkFullscriptPatient(patientId, fullscriptPatientId);
    $[0] = patientId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== patientId || $[3] !== queryClient || $[4] !== syncPlans) {
    t1 = (_data, fullscriptPatientId_0) => {
      const treatmentPlansKey = fullscriptQueryKeys.treatmentPlans(patientId);
      const previous = queryClient.getQueryData(treatmentPlansKey);
      const wasLinkedToSamePatient = Boolean(previous?.isLinked) && (previous?.fullscriptPatientId == null || previous.fullscriptPatientId === fullscriptPatientId_0);
      queryClient.setQueryData(treatmentPlansKey, cached => cached ? {
        ...cached,
        isLinked: true,
        fullscriptPatientId: fullscriptPatientId_0
      } : cached);
      if (wasLinkedToSamePatient) {
        return;
      }
      syncPlans();
    };
    $[2] = patientId;
    $[3] = queryClient;
    $[4] = syncPlans;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== t0 || $[7] !== t1) {
    t2 = {
      mutationFn: t0,
      onSuccess: t1
    };
    $[6] = t0;
    $[7] = t1;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  return useMutation(t2);
}
export function useUnlinkFullscriptPatientMutation(patientId) {
  const $ = _c(8);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== patientId) {
    t0 = () => unlinkFullscriptPatient(patientId);
    $[0] = patientId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== patientId || $[3] !== queryClient) {
    t1 = () => {
      queryClient.setQueryData(fullscriptQueryKeys.treatmentPlans(patientId), _temp3);
    };
    $[2] = patientId;
    $[3] = queryClient;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== t0 || $[6] !== t1) {
    t2 = {
      mutationFn: t0,
      onSuccess: t1
    };
    $[5] = t0;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return useMutation(t2);
}
function _temp3(previous) {
  return previous ? {
    ...previous,
    items: [],
    lastSyncedAt: null,
    isLinked: false
  } : previous;
}
export function useFullscriptTreatmentPlans(patientId) {
  const $ = _c(8);
  let t0;
  if ($[0] !== patientId) {
    t0 = fullscriptQueryKeys.treatmentPlans(patientId);
    $[0] = patientId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== patientId) {
    t1 = () => fetchFullscriptTreatmentPlans(patientId);
    $[2] = patientId;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const t2 = Boolean(patientId);
  let t3;
  if ($[4] !== t0 || $[5] !== t1 || $[6] !== t2) {
    t3 = {
      queryKey: t0,
      queryFn: t1,
      enabled: t2
    };
    $[4] = t0;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return useQuery(t3);
}
export function useSyncFullscriptTreatmentPlansMutation(patientId) {
  const $ = _c(8);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== patientId) {
    t0 = () => syncFullscriptTreatmentPlans(patientId);
    $[0] = patientId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== patientId || $[3] !== queryClient) {
    t1 = data => {
      if (data) {
        queryClient.setQueryData(fullscriptQueryKeys.treatmentPlans(patientId), data);
      }
    };
    $[2] = patientId;
    $[3] = queryClient;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== t0 || $[6] !== t1) {
    t2 = {
      mutationFn: t0,
      onSuccess: t1,
      retry: 2,
      retryDelay: _temp4,
      onError: _temp5
    };
    $[5] = t0;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return useMutation(t2);
}
function _temp5() {
  toast({
    title: "Could not load recommendations from Fullscript",
    description: "The chart is linked, but recommendations made before linking haven't been pulled in. New ones will still appear.",
    variant: "warning"
  });
}
function _temp4(attempt) {
  return 500 * 2 ** attempt;
}